import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CalendarClock, Camera, ClipboardList, MapPin, ScanSearch, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, StatusBadge, EmptyState } from "../../components/common";
import { CollectPaymentFlow, ImportBlockNotice, useImportCheck } from "../../components/CollectPaymentFlow";
import { MobileVerifyCard, MobileVerifyDrawer, useMobileVerification } from "../../components/MobileVerifyGate";
import { GpsPhotoGallery } from "../../components/GpsPhotoGallery";
import { GpsPhotoCapture, uploadPropertyPhoto, heldToQueuePhotos } from "../../components/GpsPhotoCapture";
import { VisitPhotosStep, visitPhotoCount } from "../../components/VisitPhotosStep";
import {
  MANUAL_VISIT_OUTCOMES, SameDayVisitBanner, SupervisorVisitBanner, VisitTimeline, useVisitHistory,
} from "../../components/VisitTimeline";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "../../components/ui/drawer";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { api, errMsg, fmtINR, fmtDate } from "../../lib/api";
import { gpsPhotosToday, VISIT_PHOTO_REQUIRED_MESSAGE } from "../../lib/gps";
import { useAuth } from "../../context/AuthContext";
import { StaffLabel } from "../../components/StaffAvatar";
import { CollectionSummaryCards, PaymentHistoryTable, ResponsibleStaff } from "../../components/PropertyCollectionSummary";
import { queueAdd, useOnline, isNetworkError } from "../../lib/offline";
import { useRoleModules } from "../../lib/roleModules";
import { ReadOnlyNotice } from "../../components/FeatureGate";

export default function VisitWorkspace() {
  const { code } = useParams();
  // Phase 152: "visits.field_visits" View only -> look up properties and read history, but no payments / visit logging
  const access = useRoleModules(code);
  const readOnly = !access.canWrite("visits", "field_visits");
  const { auth } = useAuth();
  const online = useOnline();
  const [assigned, setAssigned] = useState(null);
  const [pin, setPin] = useState("");
  const [searching, setSearching] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [visitOpen, setVisitOpen] = useState(false);
  const [visit, setVisit] = useState({ outcome: "", follow_up_date: "", notes: "" });
  const [visitBusy, setVisitBusy] = useState(false);
  const [heldPhotos, setHeldPhotos] = useState([]);       // Phase 132: GPS photos captured inside the visit drawer
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [deletePhoto, setDeletePhoto] = useState(null);
  const { history, reload: reloadVisits } = useVisitHistory(code, selectedId);

  const loadAssigned = useCallback(() => api.get(`/t/${code}/visits/my-assigned`)
    .then((r) => setAssigned(r.data)).catch(() => setAssigned({ items: [], total: 0 })), [code]);
  useEffect(() => { loadAssigned(); }, [loadAssigned]);

  const loadDetail = useCallback((id) => {
    setDetail(null);
    setSelectedId(id);
    setHeldPhotos([]);
    api.get(`/t/${code}/properties/${id}`).then((r) => setDetail(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [code]);
  const refreshDetail = useCallback(() => selectedId
    ? api.get(`/t/${code}/properties/${selectedId}`).then((r) => setDetail(r.data)).catch(() => {})
    : Promise.resolve(), [code, selectedId]);

  // Phase 134: the owner's mobile number must be verified (once per day, by me) before a visit / collection
  const mv = useMobileVerification({ code, property: detail?.property, verification: detail?.mobile_verification,
    userId: auth?.user?.id, online, onChanged: () => { if (online) refreshDetail(); } });
  // Phase 177: bulk-imported payment on this PIN → manual entry blocked (full) or capped to the remaining balance
  const importCheck = useImportCheck(code, detail?.property?.id, detail);

  // Phase 132: standalone "Add GPS photo" (mirrors the collector page) — only photos WITH a real GPS fix arrive here
  const onStandalonePhoto = async (photo) => {
    setUploadingPhoto(true);
    try {
      await uploadPropertyPhoto(code, selectedId, photo);
      toast.success(`Photo saved with GPS (${photo.lat.toFixed(5)}, ${photo.lng.toFixed(5)})`);
      await refreshDetail();
    } catch (err) { toast.error(errMsg(err)); } finally { setUploadingPhoto(false); }
  };

  const confirmDeletePhoto = async () => {
    try {
      await api.delete(`/t/${code}/field/property-photos/${selectedId}/${deletePhoto.file_id}`);
      toast.success("Photo removed");
      setDeletePhoto(null);
      await refreshDetail();
    } catch (err) { toast.error(errMsg(err)); setDeletePhoto(null); }
  };

  const searchPin = async (e) => {
    e?.preventDefault();
    const v = pin.trim();
    if (!v || searching) return;
    setSearching(true);
    try {
      const r = await api.get(`/t/${code}/properties/pin-lookup`, { params: { pin: v } });
      toast.success(`${r.data.pin} — ${r.data.owner_name || "Property found"}`);
      setPin("");
      loadDetail(r.data.id);
    } catch (err) { toast.error(errMsg(err)); } finally { setSearching(false); }
  };

  const closeVisitDrawer = () => {
    setVisitOpen(false);
    setVisit({ outcome: "", follow_up_date: "", notes: "" });
    setHeldPhotos([]);
  };

  const submitVisit = async () => {
    const cfg = MANUAL_VISIT_OUTCOMES.find((o) => o.value === visit.outcome);
    if (!cfg) return toast.error("Select the visit outcome");
    if (cfg.needsDate && !visit.follow_up_date) return toast.error("Select the date");
    if (cfg.needsReason && !visit.notes.trim()) return toast.error("Enter the reason for no payment");
    // Phase 132: at least one GPS photo (captured now, or earlier today by me) is mandatory
    if (visitPhotoCount(heldPhotos, detail?.property, auth?.user?.id) === 0) return toast.error(VISIT_PHOTO_REQUIRED_MESSAGE);
    setVisitBusy(true);
    const todayIds = gpsPhotosToday(detail?.property?.property_photos, auth?.user?.id).map((p) => p.file_id);
    // Phase 184: device clock at the moment of saving — sent online and offline alike
    const visitedAt = new Date().toISOString();
    const payload = {
      property_id: selectedId, outcome: visit.outcome,
      follow_up_date: visit.follow_up_date || null, notes: visit.notes || null,
      photo_file_ids: todayIds, visited_at: visitedAt,
    };
    const offlineSave = async () => {
      await queueAdd(code, "visit", { ...payload },
        { photos: heldToQueuePhotos(heldPhotos), label: `Visit • ${detail?.property?.pin} (${cfg.label})` });
      toast.success("Visit saved on device — syncs (and notifies the collector) when back online");
      closeVisitDrawer();
    };
    try {
      if (!online) { await offlineSave(); return; }
      try {
        const uploaded = [];
        for (const photo of heldPhotos) {
          uploaded.push((await uploadPropertyPhoto(code, selectedId, photo)).file_id);
        }
        const saved = await api.post(`/t/${code}/field/visit-log`, { ...payload, photo_file_ids: [...todayIds, ...uploaded] });
        // Phase 217: a second visitor's visit-only log is stored but not counted in their name
        if (saved.data?.same_day_notice) toast.warning(saved.data.same_day_notice, { duration: 8000 });
        else toast.success("Visit recorded — the mapped tax collector has been notified");
        closeVisitDrawer();
        reloadVisits();
        loadAssigned();
        refreshDetail();
      } catch (err) {
        if (isNetworkError(err)) { await offlineSave(); return; }
        toast.error(errMsg(err));
      }
    } finally { setVisitBusy(false); }
  };

  const property = detail?.property;
  const allPhotos = [...(property?.third_party_photos || []), ...(property?.property_photos || [])];
  const pendingDemands = (detail?.demands || []).filter((d) => d.balance_amount > 0);
  const totalDue = pendingDemands.reduce((s, d) => s + d.balance_amount, 0);

  return (
    <div>
      <PageHeader title="Field Visits & Collection"
        subtitle="Visit any property, record the outcome and collect payment — the mapped collector is notified automatically" />

      {!online && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 flex items-center gap-2 text-amber-900 mb-4" data-testid="visits-offline-banner">
          <WifiOff className="h-4 w-4 shrink-0" />
          <p className="text-xs">Offline — visit outcomes are stored on this device and sync automatically. Payment collection needs internet.</p>
        </div>
      )}

      <div className="grid lg:grid-cols-[380px_1fr] gap-4 items-start">
        {/* LEFT: search + assigned queue */}
        <div className="space-y-4">
          <Card className="shadow-sm">
            <CardContent className="p-4">
              <p className="font-medium text-sm mb-2 flex items-center gap-1.5"><ScanSearch className="h-4 w-4" /> Find a property</p>
              <form onSubmit={searchPin} className="flex gap-2">
                <Input value={pin} onChange={(e) => setPin(e.target.value)} placeholder="Enter / scan PIN..."
                  className="font-mono" data-testid="visit-pin-input" />
                <Button type="submit" disabled={searching || !pin.trim()} data-testid="visit-pin-search-button">
                  {searching ? "..." : "Open"}
                </Button>
              </form>
              <p className="text-[11px] text-muted-foreground mt-1.5">You can open any property in the municipality.</p>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="p-4">
              <p className="font-medium text-sm mb-2 flex items-center gap-1.5" data-testid="my-assigned-title">
                <ClipboardList className="h-4 w-4" /> Assigned to me {assigned ? `(${assigned.total})` : ""}
              </p>
              {!assigned ? <Spinner /> : assigned.items.length === 0 ? (
                <p className="text-xs text-muted-foreground" data-testid="my-assigned-empty">
                  No properties assigned to you yet. The admin can assign priority properties for your field visits.
                </p>
              ) : (
                <div className="space-y-2">
                  {assigned.items.map((a) => (
                    <button key={a.id} onClick={() => loadDetail(a.property_id)}
                      className={`w-full text-left border rounded-lg px-3 py-2 transition-colors hover:bg-muted/60 ${selectedId === a.property_id ? "border-primary bg-muted/50" : ""}`}
                      data-testid={`assigned-property-${a.pin}`}>
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-mono text-sm font-semibold">{a.pin}</p>
                        <p className="text-sm font-semibold tabular-nums text-destructive">{fmtINR(a.balance)}</p>
                      </div>
                      <p className="text-xs">{a.owner_name || a.property?.owner_name || "-"}</p>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-[11px] text-muted-foreground">{a.property?.ward_label || "-"}</p>
                        <Badge variant="outline" className="text-[10px]">{a.visit_count} visit{a.visit_count === 1 ? "" : "s"}</Badge>
                      </div>
                      {a.property?.last_visit_at && (
                        <p className="text-[11px] text-muted-foreground mt-0.5">
                          Last: {fmtDate(a.property.last_visit_at.slice(0, 10))} by <StaffLabel code={code} userId={a.property.last_visit_by} name={a.property.last_visit_by_name} fallback="-" />
                        </p>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* RIGHT: selected property workspace */}
        <div className="space-y-4">
          {!selectedId ? (
            <EmptyState title="Pick a property to start"
              subtitle="Search a PIN or choose one of your assigned properties to record a visit or collect payment" />
          ) : !detail ? <Spinner /> : (
            <>
              <Card className="shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div>
                      <p className="font-mono font-semibold" data-testid="workspace-property-pin">{property.pin}</p>
                      <p className="font-medium">{property.owner_name}</p>
                      <p className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1"><MapPin className="h-3 w-3" /> {property.address || "-"} • {property.ward_label || "-"}</p>
                    </div>
                    <div className="text-right">
                      <StatusBadge status={property.payment_status} />
                      <p className="font-semibold tabular-nums text-destructive mt-1" data-testid="workspace-total-due">{fmtINR(totalDue)} due</p>
                    </div>
                  </div>
                  {property.assignment && (
                    <p className="text-xs text-muted-foreground mt-2" data-testid="workspace-mapped-collector">
                      Mapped collector: <span className="font-medium text-foreground"><StaffLabel code={code} userId={property.assignment.tax_collector_id} name={property.assignment.collector_name} /></span> — they are notified of your visits & collections
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Phase 134: verify (confirm / update) the owner's mobile number — gates Record Visit + Collect Payment */}
              <MobileVerifyCard mv={mv} testId="supervisor-mobile-verify" />

              <SupervisorVisitBanner summary={history?.summary} />
              <SameDayVisitBanner sameDay={history?.same_day} />

              {/* Phase 132: standalone GPS photo capture — mirrors the collector's property page */}
              <Card className="shadow-sm" data-testid="workspace-photos-card">
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium text-sm flex items-center gap-1.5">
                      <Camera className="h-4 w-4" /> Property photos
                    </p>
                    <GpsPhotoCapture testId="supervisor-add-photo" busy={uploadingPhoto} disabled={!online} onCapture={onStandalonePhoto} />
                  </div>
                  {allPhotos.length === 0 ? (
                    <p className="text-xs text-muted-foreground" data-testid="workspace-photos-empty">
                      No photos yet — capture the site during your visit. Every photo is stamped with your GPS location (location must be on).
                    </p>
                  ) : (
                    <GpsPhotoGallery code={code} photos={allPhotos} title="GPS-tagged photos"
                      canDelete={(p) => p.uploaded_by === auth?.user?.id}
                      onDelete={(p) => setDeletePhoto(p)} />
                  )}
                </CardContent>
              </Card>

              {/* Phase 104: totals, accountable staff and the full receipt history */}
              <Card className="shadow-sm" data-testid="workspace-collection-card">
                <CardContent className="p-4 space-y-3">
                  <p className="font-medium text-sm">Collection summary</p>
                  <CollectionSummaryCards summary={detail.collection_summary} testId="workspace-collection-summary" />
                  <ResponsibleStaff code={code} responsible={detail.responsible} testId="workspace-responsible" />
                </CardContent>
              </Card>

              {pendingDemands.length > 0 && (
                <Card className="shadow-sm">
                  <CardContent className="p-4 space-y-2">
                    <p className="font-medium text-sm">Pending demands</p>
                    {pendingDemands.map((d) => (
                      <div key={d.id} className="flex items-center justify-between border rounded-lg px-3 py-2">
                        <div>
                          <p className="text-sm font-medium">{d.tax_head_name}</p>
                          <p className="text-[11px] text-muted-foreground">{d.fy_label}</p>
                        </div>
                        <p className="text-sm font-semibold tabular-nums">{fmtINR(d.balance_amount)}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {!readOnly && <ImportBlockNotice check={importCheck.check} testId="supervisor-import-check" />}
              {readOnly ? <ReadOnlyNotice what="field visits" testId="field-visits-readonly-notice" /> : (
              <div className="grid grid-cols-2 gap-3">
                <CollectPaymentFlow code={code} property={property} pendingDemands={pendingDemands}
                  disabled={totalDue <= 0 || !online} gate={mv.gate} importCheck={importCheck.check} />
                <Button size="lg" variant="secondary" className="h-12" onClick={() => mv.gate(() => setVisitOpen(true))}
                  data-testid="supervisor-record-visit-button">
                  <CalendarClock className="h-4 w-4 mr-1.5" /> Record Visit
                </Button>
              </div>
              )}

              <Card className="shadow-sm" data-testid="workspace-collection-history-card">
                <CardContent className="p-4">
                  <p className="font-medium text-sm mb-3">Collection history{detail.payments?.length ? ` (${detail.payments.length})` : ""}</p>
                  <PaymentHistoryTable code={code} payments={detail.payments} testId="workspace-collection-history" />
                </CardContent>
              </Card>

              <Card className="shadow-sm">
                <CardContent className="p-4">
                  <p className="font-medium text-sm mb-3" data-testid="workspace-visit-history-title">
                    Visit history{history ? ` (${history.summary.total_visits})` : ""}
                  </p>
                  <VisitTimeline code={code} visits={history?.visits || []} />
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>

      {/* delete own photo confirm (Phase 132) */}
      <AlertDialog open={!!deletePhoto} onOpenChange={(v) => !v && setDeletePhoto(null)}>
        <AlertDialogContent data-testid="supervisor-photo-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this photo?</AlertDialogTitle>
            <AlertDialogDescription>The photo will be removed from this property. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="supervisor-photo-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeletePhoto}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="supervisor-photo-delete-confirm-button">Remove photo</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <MobileVerifyDrawer mv={mv} testId="supervisor-mobile-verify" />

      <Drawer open={visitOpen} onOpenChange={(v) => (v ? setVisitOpen(true) : closeVisitDrawer())}>
        <DrawerContent className="max-h-[92vh]">
          <DrawerHeader><DrawerTitle className="font-heading">Record visit outcome</DrawerTitle></DrawerHeader>
          <div className="px-4 pb-6 space-y-4 overflow-y-auto max-w-lg mx-auto w-full">
            <div className="space-y-1">
              <Label>What happened at the property? *</Label>
              <Select value={visit.outcome} onValueChange={(v) => v && setVisit((s) => ({ ...s, outcome: v }))}>
                <SelectTrigger className="h-11" data-testid="supervisor-visit-outcome-select">
                  <SelectValue placeholder="Select outcome">{(MANUAL_VISIT_OUTCOMES.find((o) => o.value === visit.outcome) || {}).label || null}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {MANUAL_VISIT_OUTCOMES.map((o) => (
                    <SelectItem key={o.value} value={o.value} data-testid={`supervisor-visit-outcome-option-${o.value}`}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>
                {visit.outcome === "preferred_date" ? "Owner's preferred collection date *"
                  : visit.outcome === "next_visit" ? "Next visit date *" : "Follow-up date (optional)"}
              </Label>
              <Input type="date" className="h-11" value={visit.follow_up_date}
                onChange={(e) => setVisit((s) => ({ ...s, follow_up_date: e.target.value }))} data-testid="supervisor-visit-date-input" />
            </div>
            <div className="space-y-1">
              <Label>{visit.outcome === "no_payment" ? "Reason for no payment *" : "Remarks"}</Label>
              <Textarea rows={2} value={visit.notes} onChange={(e) => setVisit((s) => ({ ...s, notes: e.target.value }))}
                placeholder={visit.outcome === "no_payment" ? "e.g. Owner said salary not credited yet" : "e.g. Owner asked to come after salary day"}
                data-testid="supervisor-visit-notes-input" />
            </div>
            {/* Phase 132: at least one GPS photo is mandatory to record the visit */}
            <VisitPhotosStep code={code} property={property} userId={auth?.user?.id} held={heldPhotos} setHeld={setHeldPhotos} testId="supervisor-visit-photos" />
            <p className="text-[11px] text-muted-foreground">
              The mapped tax collector receives an in-app notification with your visit details, and the owner is notified externally.
            </p>
            <Button size="lg" className="w-full h-12" onClick={submitVisit}
              disabled={visitBusy || visitPhotoCount(heldPhotos, property, auth?.user?.id) === 0}
              data-testid="supervisor-visit-submit-button">
              {visitBusy ? "Saving..." : "Save visit & notify"}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
